import { HttpEvent, HttpEventType } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { Observable, Observer, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AlertService, AlertType } from '../../services/alert/alert.service';
import { S3FileManagerService } from '../../services/s3-file-manager/s3-file-manager.service';
import { ModalComponent, ModalConfig } from '../modals/modal/modal.component';

interface StagedUploadFile {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'done' | 'error';
  errorMsg: string;
}

@Component({
  selector: 'app-s3-file-upload-modal',
  standalone: false,
  templateUrl: './s3-file-upload-modal.component.html',
  styleUrl: './s3-file-upload-modal.component.scss',
})
export class S3FileUploadModalComponent {
  @Input() currentPath = '';

  @Output() refreshList = new EventEmitter<void>();

  @ViewChild('modal') modal: ModalComponent;

  static readonly ALLOWED_EXTENSIONS = new Set([
    'pdf',
    'csv',
    'doc',
    'docx',
    'xls',
    'xlsx',
    'html',
    'txt',
    'md',
    'gif',
    'jpeg',
    'jpg',
    'png',
  ]);

  static readonly MAX_FILE_SIZE_BYTES = 1 * 1024 * 1024 * 1024; // 1 GB

  readonly modalConfig: ModalConfig = {
    titleText: 'Upload Files',
    headerClass: ['modal-header', 'bg-primary', 'text-white'],
    customFooter: true,
    hideCloseButton: true,
  };

  readonly getFileIcon = S3FileUploadModalComponent.getFileIcon;

  stagedFiles: StagedUploadFile[] = [];

  uploadSaving = false;

  uploadDropzoneActive = false;

  uploadErrorMessage = '';

  constructor(
    private alertService: AlertService,
    private s3FileManagerService: S3FileManagerService
  ) {}

  open(): void {
    this.modal.open();
  }

  onUploadFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.stageFiles(Array.from(input.files));
      input.value = '';
    }
  }

  handleUploadFileDrop(event: DragEvent): void {
    event.preventDefault();
    this.uploadDropzoneActive = false;
    if (event.dataTransfer?.files.length) {
      this.stageFiles(Array.from(event.dataTransfer.files));
    }
  }

  stageFiles(files: File[]): void {
    const rejected: string[] = [];
    const valid: File[] = [];
    files.forEach((file) => {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
      if (!S3FileUploadModalComponent.ALLOWED_EXTENSIONS.has(ext)) {
        rejected.push(`"${file.name}" is not a supported file type`);
      } else if (file.size > S3FileUploadModalComponent.MAX_FILE_SIZE_BYTES) {
        rejected.push(`"${file.name}" exceeds the 1 GB size limit`);
      } else {
        valid.push(file);
      }
    });
    if (rejected.length) {
      this.uploadErrorMessage = rejected.join('; ');
    }
    const newEntries: StagedUploadFile[] = valid.map((file) => ({
      file,
      progress: 0,
      status: 'pending',
      errorMsg: '',
    }));
    this.stagedFiles = [...this.stagedFiles, ...newEntries];
  }

  removeStagedFile(index: number): void {
    this.stagedFiles = this.stagedFiles.filter((_, i) => i !== index);
  }

  private handleUploadProgress(
    event: HttpEvent<unknown> | null,
    index: number,
    observer: Observer<null>
  ): void {
    if (!event) {
      observer.next(null);
      observer.complete();
      return;
    }
    if (event.type === HttpEventType.UploadProgress && event.total) {
      this.stagedFiles[index].progress = Math.round(
        (100 * event.loaded) / event.total
      );
    } else if (event.type === HttpEventType.Response) {
      this.stagedFiles[index].status = 'done';
      this.stagedFiles[index].progress = 100;
      observer.next(null);
      observer.complete();
    }
  }

  private createUploadStream(
    staged: StagedUploadFile,
    index: number
  ): Observable<null> {
    staged.status = 'uploading';
    return new Observable<null>((observer) => {
      this.s3FileManagerService
        .uploadFileWithProgress(staged.file, this.currentPath || undefined)
        .pipe(
          catchError(() => {
            this.stagedFiles[index].status = 'error';
            this.stagedFiles[index].errorMsg = 'Upload failed';
            return of(null);
          })
        )
        .subscribe({
          next: (event) => this.handleUploadProgress(event, index, observer),
          error: () => {
            observer.next(null);
            observer.complete();
          },
        });
    });
  }

  private onAllUploadsComplete(): void {
    this.uploadSaving = false;
    const anyErrors = this.stagedFiles.some((f) => f.status === 'error');
    if (!anyErrors) {
      this.modal.close();
      this.refreshList.emit();
      this.alertService.showAlert(
        'Files uploaded successfully',
        undefined,
        AlertType.PRIMARY
      );
    } else {
      this.refreshList.emit();
    }
  }

  saveUploadFiles(): void {
    if (!this.stagedFiles.length) return;
    this.uploadSaving = true;
    const streams = this.stagedFiles.map((staged, index) =>
      this.createUploadStream(staged, index)
    );
    forkJoin(streams).subscribe(() => this.onAllUploadsComplete());
  }

  reset(): void {
    this.stagedFiles = [];
    this.uploadSaving = false;
    this.uploadDropzoneActive = false;
    this.uploadErrorMessage = '';
  }

  static getFileIcon(key: string): string {
    if (key.endsWith('/')) return 'bi-folder-fill';
    const ext = key.split('.').pop()?.toLowerCase() ?? '';
    const iconMap: Record<string, string> = {
      png: 'bi-file-image',
      jpg: 'bi-file-image',
      jpeg: 'bi-file-image',
      gif: 'bi-file-image',
      svg: 'bi-file-image',
      mp4: 'bi-file-play',
      mov: 'bi-file-play',
      avi: 'bi-file-play',
      mkv: 'bi-file-play',
      mp3: 'bi-file-music',
      wav: 'bi-file-music',
      pdf: 'bi-file-pdf',
      doc: 'bi-file-word',
      docx: 'bi-file-word',
      xls: 'bi-file-earmark-spreadsheet',
      xlsx: 'bi-file-earmark-spreadsheet',
      csv: 'bi-file-earmark-spreadsheet',
      zip: 'bi-file-zip',
      gz: 'bi-file-zip',
      txt: 'bi-file-text',
      md: 'bi-file-text',
      json: 'bi-file-code',
      xml: 'bi-file-code',
      js: 'bi-file-code',
      ts: 'bi-file-code',
    };
    return iconMap[ext] ?? 'bi-file';
  }
}
